/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable  @typescript-eslint/no-unused-vars */
import {Injectable} from '@angular/core';
import {AlertModalService} from '../../../../common/component/modal';
import {EnlargementUtilService} from './enlargement-util.service';
import {SpatialUtilService} from '../../../services/spatial-util.service';
import {MapModeService} from '../../../services/map-mode.service';
import {EsriModuleService} from '../../../esri-nlr/services/esri-module.service';
import {catchError, interval, Observable, of, take, tap, throwError} from 'rxjs';
import {FeatureApplyDeleteService} from '../../../services/feature-apply-delete.service';
import {filter, switchMap} from 'rxjs/operators';
import {forkJoinDefaultIfEmpty} from '../../../../common/observable.utils';
import {NgEventBus} from 'ng-event-bus';

@Injectable({
    providedIn: 'root'
})
export class EnlargementDeleteService {
    constructor(
        private alertModalService: AlertModalService,
        private featuresApplyDeleteService: FeatureApplyDeleteService,
        private enlargementUtilService: EnlargementUtilService,
        private spatialUtilService: SpatialUtilService,
        private esriModuleService: EsriModuleService,
        private mapModeService: MapModeService,
        private eventBus: NgEventBus
    ) {}
    title = 'EnlargementDeleteService';
    /**
     * Deletes the given clipping feature.
     */
    deleteClippingFeature(clippingFeatures) {
        if (!clippingFeatures || (clippingFeatures && clippingFeatures.length === 0)) {
            return;
        }

        // delete the clipping feature
        this.featuresApplyDeleteService
            .commitFeaturesDeletion(this.spatialUtilService.getClippingMapLayer(), clippingFeatures, false)
            .subscribe(data => {
                const deletedPolygons = data as any[];
                if (deletedPolygons && deletedPolygons.length > 0) {
                    console.debug('Clipping polygon has been successfully deleted!');
                } else {
                    console.debug('Error deleting clipping polygon!');
                    this.alertModalService.createAlertMessage('Error occurred deleting clipping polygon feature.');
                }
            });
    }

    /**
     * Deletes the given enlargement feature, removing the enlargement id from the parent feature/s attributes, then deleting all related angles,
     * annotations and finally the enlargement feature itself.
     */
    deleteEnlargement(enlargementFeature: any) {
        const enlargementId = enlargementFeature.attributes.id;

        const updateRelatedAngles = this.findAllAnglesRelatedToEnlargement(enlargementId).pipe(
            switchMap((angles: any[]) => {
                if (angles.length === 0) {
                    return of(null);
                }

                const updatedAngles = angles.map(angle => {
                    const updatedAngle = new this.esriModuleService.Graphic(angle.geometry);
                    updatedAngle.geometry = window.structuredClone(angle.geometry);
                    updatedAngle.attributes = window.structuredClone(angle.attributes);
                    this.enlargementUtilService.removeEnlargementId(updatedAngle, enlargementId);
                    return updatedAngle;
                });

                return this.spatialUtilService.applyEdits('angle', null, updatedAngles, null).pipe(
                    tap(res => {
                        if (res.updates && res.updates.length > 0) {
                            console.debug('Success updating angles!');
                        } else {
                            console.error('Error updating angles.');
                        }
                    }),
                    catchError(err => {
                        console.error('Error updating angles: ', err.message || err);
                        return throwError(() => new Error(err));
                    })
                );
            })
        );

        const updateRelatedAnnotations = this.findAllAnnotationsRelatedToEnlargement(enlargementId).pipe(
            switchMap((annotations: any[]) => {
                if (annotations.length === 0) {
                    return of(null);
                }

                const updatedAnnotations = annotations.map(annotation => {
                    const updatedAnnotation = new this.esriModuleService.Graphic(this.spatialUtilService.copyGeometry(annotation.geometry));
                    updatedAnnotation.attributes = structuredClone(annotation.attributes);
                    this.enlargementUtilService.removeEnlargementId(updatedAnnotation, enlargementId);
                    return updatedAnnotation;
                });

                return this.spatialUtilService.applyEdits('annotation', null, updatedAnnotations, null).pipe(
                    tap(res => {
                        if (res.updates && res.updates.length > 0) {
                            console.debug('Success updating annotations!');
                        } else {
                            console.error('Error updating annotations.');
                        }
                    }),
                    catchError(err => {
                        console.error('Error updating annotations: ', err.message || err);
                        return throwError(() => new Error(err));
                    })
                );
            })
        );

        return forkJoinDefaultIfEmpty([updateRelatedAngles, updateRelatedAnnotations]).pipe(
            switchMap(() => this.deleteAllEnlargementFeatures(enlargementFeature)),
            catchError(err => {
                console.error('Error in updating related angles or annotations: ', err);
                return throwError(() => new Error(err));
            })
        );
    }

    deleteAllEnlargementFeatures(enlargementFeature: any): Observable<[void, void]> {
        const deleteAnnotations = this.deleteEnlargementFeaturesByType(enlargementFeature, this.spatialUtilService.getFeatureLayer('annotation'));
        const deleteAngles = this.deleteEnlargementFeaturesByType(enlargementFeature, this.spatialUtilService.getFeatureLayer('angle'));

        return forkJoinDefaultIfEmpty([deleteAnnotations, deleteAngles]).pipe(
            tap(() => {
                // Remove the enlargement feature
                this.deleteItems(enlargementFeature);
            })
        );
    }
    deleteEnlargementFeaturesByType(enlargementFeature: any, featureLayer: any): Observable<void> {
        const features = this.spatialUtilService.getFeatures(featureLayer);

        const featuresToBeDeleted = features.filter(
            feature => enlargementFeature.attributes && feature.attributes && enlargementFeature.attributes.id === feature.attributes.enlargementid
        );

        return new Observable<void>(observer => {
            this.deleteFeatures(featureLayer, featuresToBeDeleted, false, (deletedFeatures: any) => {
                observer.next();
                observer.complete();
            });
        });
    }

    /**
     * Deletes the enlargement feature, removes the enlargement id from parent features in the main map (if applicable).
     */
    deleteItems(enlargementFeature: any): void {
        const subs = this.deleteEnlargementFeature(enlargementFeature)
            .pipe(
                //from map-mode.service
                tap(() => {
                    console.debug(this.title + '  : removeEnlargementOnMapModeList. ', enlargementFeature.attributes.id);
                    const indexToDelete = this.mapModeService.mapModeList.findIndex(
                        item => item.id === enlargementFeature.attributes.id && item.type !== 'mainPlan'
                    );
                    if (indexToDelete > -1) {
                        this.mapModeService.mapModeList.splice(indexToDelete, 1);
                    }
                    // Only need to zoom to plan extent when changing from an enlargement to the main plan
                    const zoomPlanExtent = this.mapModeService.isMapModeType('enlargement');
                    this.mapModeService.mapMode = this.mapModeService.mapModeList[0];
                    this.mapModeService.mapModeChange({zoomPlanExtent: zoomPlanExtent});
                }),
                switchMap(() => {
                    const checkMapModeAndUpdateAnnotations$ = interval(600).pipe(
                        take(5),
                        filter(() => this.enlargementUtilService.isMapModeMainPlan() && this.spatialUtilService.getAnnotations().length > 0),
                        take(1),
                        switchMap(() => {
                            const enlargementAnnotations = this.findFeaturesWithinEnlargement(
                                enlargementFeature,
                                this.spatialUtilService.getAnnotationMapLayer()
                            );

                            if (enlargementAnnotations.length > 0) {
                                this.enlargementUtilService.updateEnlargementIdFromFeatures(enlargementFeature.attributes.id, enlargementAnnotations);

                                return this.spatialUtilService.applyEdits('annotation', null, enlargementAnnotations, null).pipe(
                                    tap(res => console.debug(this.title + ' : updateAnnotations - updates - RESULT: ' + JSON.stringify(res.updates))),
                                    catchError(err => {
                                        this.alertModalService.createAlertMessage('Error updating annotation features. Error : ' + err, 'Ok');
                                        return throwError(() => new Error(err));
                                    })
                                );
                            }
                            return of();
                        })
                    );

                    const checkMapModeAndUpdateAngles$ = interval(600).pipe(
                        take(5),
                        filter(() => this.enlargementUtilService.isMapModeMainPlan() && this.spatialUtilService.getAngles().length > 0),
                        take(1),
                        switchMap(() => {
                            const enlargementAngles = this.findFeaturesWithinEnlargement(
                                enlargementFeature,
                                this.spatialUtilService.getAngleMapLayer()
                            );

                            if (enlargementAngles.length > 0) {
                                this.enlargementUtilService.updateEnlargementIdFromFeatures(enlargementFeature.attributes.id, enlargementAngles);

                                return this.spatialUtilService.applyEdits('angle', null, enlargementAngles, null).pipe(
                                    tap(res => console.debug(this.title + ' : updateAngles - updates - RESULT: ' + JSON.stringify(res.updates))),
                                    catchError(err => {
                                        this.alertModalService.createAlertMessage('Error updating angle features. Error : ' + err, 'Ok');
                                        return throwError(() => new Error(err));
                                    })
                                );
                            }
                            return of();
                        })
                    );

                    return forkJoinDefaultIfEmpty([checkMapModeAndUpdateAnnotations$, checkMapModeAndUpdateAngles$]);
                })
            )
            .subscribe();
        this.eventBus.cast('nlr:busy', subs);
    }

    /**
     * Removes the given enlargement from the enlargement feature layer.
     */
    deleteEnlargementFeature(feature) {
        return this.featuresApplyDeleteService.commitFeaturesDeletion(this.spatialUtilService.getEnlargementMapLayer(), [feature], false).pipe(
            tap(data => {
                const deletedPolygons = data as any[];
                if (deletedPolygons && deletedPolygons.length > 0) {
                    console.debug('Enlargement polygon has been successfully deleted!');
                } else {
                    console.debug('Error deleting enlargement polygon!');
                    this.alertModalService.createAlertMessage('Error occurred deleting enlargement polygon feature.');
                }
            })
        );
    }

    /**
     * Deletes the list of features from a given layer.
     */
    deleteFeatures(layer: any, features: any[], anglesFlag: boolean, callback: any) {
        if (features && features.length > 0) {
            this.featuresApplyDeleteService.commitFeaturesDeletion(layer, features, anglesFlag).subscribe(meta => {
                const deletedFeatures = meta as any[];
                if (deletedFeatures && deletedFeatures.length > 0) {
                    console.debug('Enlargement features have been successfully deleted!');
                    callback(deletedFeatures);
                } else {
                    console.debug('Error deleting enlargement features!');
                    this.alertModalService.createAlertMessage('Error occurred deleting enlargement features.');
                    callback(null);
                }
            });
        } else {
            callback(null);
        }
    }

    /**
     * Find all angles for the given enlargement id.
     */
    findAllAnglesRelatedToEnlargement(enlargementId) {
        return of(
            this.spatialUtilService
                .getAngleGraphics()
                .filter(f => f.attributes.enlargementids && f.attributes.enlargementids.includes(`[${enlargementId}]`))
        );
    }

    /**
     * Find all annotations for the given enlargement id.
     */
    findAllAnnotationsRelatedToEnlargement(enlargementId) {
        return of(
            this.spatialUtilService
                .getAnnotationGraphics()
                .filter(f => f.attributes.enlargementids && f.attributes.enlargementids.includes(`[${enlargementId}]`))
        );
    }

    /**
     * Find all features for the given enlargement feature and layer..
     */
    findFeaturesWithinEnlargement(enlargementFeature, layer) {
        return this.spatialUtilService
            .getGraphics(layer)
            .filter(
                g =>
                    enlargementFeature.attributes &&
                    g.attributes &&
                    g.attributes.enlargementids &&
                    this.enlargementUtilService.isWithinEnlargement(g, enlargementFeature.attributes.id)
            );
    }
}
